import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';

import { Input } from '@/components/ui/input';
import { clampForWhiteText, hexToHsl, hslToHex, parseHex, type Hsl } from '@/utils/color';

/**
 * Colour picker in a Radix popover (meeting 2026-10-22, item 27): preset
 * swatches, hue and lightness sliders, and a hex field. The native
 * `<input type="color">` is banned (eslint.web-rules.js), so the sliders
 * are hand-built with `role="slider"`.
 *
 * Every pick keeps white text readable (3:1): a colour that is too light
 * is darkened, and the popover says so.
 */
export interface ColorPickerPopoverProps {
    /** `#rrggbb`, or null when nothing has been picked yet. */
    value: string | null;
    /** Called with `#rrggbb` on every pick, while dragging too. */
    onChange: (hex: string) => void;
    onOpenChange?: (open: boolean) => void;
    /** The trigger element. */
    children: React.ReactNode;
}

const SWATCHES = [
    '#dc2626', '#ea580c', '#b45309', '#4d7c0f', '#059669', '#0d9488',
    '#0891b2', '#4f46e5', '#7c3aed', '#c026d3', '#db2777', '#78350f',
];

const DEFAULT_COLOR = '#c026d3';

const hslCss = (h: number, s: number, l: number) => `hsl(${h} ${s * 100}% ${l * 100}%)`;

export function ColorPickerPopover({ value, onChange, onOpenChange, children }: ColorPickerPopoverProps) {
    const [open, setOpen] = React.useState(false);
    const [hsl, setHsl] = React.useState<Hsl>(() => hexToHsl(value ?? DEFAULT_COLOR));
    const [text, setText] = React.useState(value ?? DEFAULT_COLOR);
    const [notice, setNotice] = React.useState<string | null>(null);

    const handleOpenChange = (next: boolean) => {
        if (next) {
            const start = value ?? DEFAULT_COLOR;
            setHsl(hexToHsl(start));
            setText(start);
            setNotice(null);
        }
        setOpen(next);
        onOpenChange?.(next);
    };

    const pick = (next: Hsl) => {
        const clamped = clampForWhiteText(next);
        const hex = hslToHex(clamped);
        setHsl(clamped);
        setText(hex);
        setNotice(clamped.l < next.l ? `Too light for white text: darkened to ${hex}` : null);
        onChange(hex);
    };

    const commitText = () => {
        const hex = parseHex(text);
        if (hex) pick(hexToHsl(hex));
        else setText(hslToHex(hsl));
    };

    const hex = hslToHex(hsl);

    return (
        <Popover.Root open={open} onOpenChange={handleOpenChange}>
            <Popover.Trigger asChild>{children}</Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    align="end"
                    sideOffset={8}
                    // z-[10000] like DatePicker / StyledSelect.
                    className="z-[10000] w-64 space-y-3 rounded-md border border-gray-200 bg-white p-3 shadow-lg"
                    aria-label="Custom colour"
                >
                    <div className="grid grid-cols-6 gap-2">
                        {SWATCHES.map((swatch) => (
                            <button
                                key={swatch}
                                type="button"
                                onClick={() => pick(hexToHsl(swatch))}
                                className={`h-7 w-7 rounded-full transition-transform hover:scale-110 ${
                                    swatch === hex ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                                }`}
                                style={{ background: swatch }}
                                aria-label={swatch}
                            />
                        ))}
                    </div>
                    <Slider
                        label="Hue"
                        value={Math.round(hsl.h)}
                        min={0}
                        max={359}
                        background={`linear-gradient(to right, ${[0, 60, 120, 180, 240, 300, 360]
                            .map((h) => hslCss(h, Math.max(hsl.s, 0.7), 0.5))
                            .join(', ')})`}
                        // A grey has no hue to move: give it some colour first.
                        onChange={(h) => pick({ ...hsl, h, s: hsl.s < 0.05 ? 0.7 : hsl.s })}
                    />
                    <Slider
                        label="Lightness"
                        value={Math.round(hsl.l * 100)}
                        min={0}
                        max={100}
                        background={`linear-gradient(to right, #000, ${hslCss(hsl.h, hsl.s, 0.5)}, #fff)`}
                        onChange={(l) => pick({ ...hsl, l: l / 100 })}
                    />
                    <div className="flex items-center gap-2">
                        <Input
                            aria-label="Hex colour"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onBlur={commitText}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commitText();
                            }}
                            className="h-8 font-mono text-sm"
                        />
                        <div
                            className="shrink-0 rounded-md px-3 py-1.5 text-sm font-medium text-white"
                            style={{ background: hex }}
                            data-testid="color-preview"
                        >
                            Button
                        </div>
                    </div>
                    {notice && (
                        <p role="status" className="text-xs text-gray-600">
                            {notice}
                        </p>
                    )}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

interface SliderProps {
    label: string;
    value: number;
    min: number;
    max: number;
    background: string;
    onChange: (value: number) => void;
}

const KEY_STEPS: Record<string, number> = {
    ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 10, PageDown: -10,
};

function Slider({ label, value, min, max, background, onChange }: SliderProps) {
    const clamp = (v: number) => Math.min(max, Math.max(min, v));

    const fromPointer = (e: React.PointerEvent<HTMLDivElement>) => {
        const box = e.currentTarget.getBoundingClientRect();
        const fraction = box.width ? (e.clientX - box.left) / box.width : 0;
        onChange(clamp(Math.round(min + fraction * (max - min))));
    };

    return (
        <div
            role="slider"
            tabIndex={0}
            aria-label={label}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            className="relative h-4 cursor-pointer touch-none rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            style={{ background }}
            onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                fromPointer(e);
            }}
            onPointerMove={(e) => {
                if (e.currentTarget.hasPointerCapture(e.pointerId)) fromPointer(e);
            }}
            onKeyDown={(e) => {
                let next: number | null = null;
                if (e.key in KEY_STEPS) next = clamp(value + KEY_STEPS[e.key]);
                else if (e.key === 'Home') next = min;
                else if (e.key === 'End') next = max;
                if (next === null) return;
                e.preventDefault();
                onChange(next);
            }}
        >
            <span
                className="pointer-events-none absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow ring-1 ring-gray-300"
                style={{ left: `${((value - min) / (max - min)) * 100}%` }}
            />
        </div>
    );
}
